import React, { PureComponent } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Buffer } from 'buffer';
import { getApplePayPaymentSession, updatePayment } from '../../../services/Api';
import { PaymentPartial } from '../../../types/Payment';
import withLocaleContext from '../../../hoc/withLocaleContext';
import withContext from '../../../hoc/withContext';
import withApplePayConfigurationContext from '../../../hoc/WithApplePayConfigurationContext';
import ApplePayButtonContainer from './ApplePayButton.styles';
import { ApplePayItem } from '../../../types/applepay/ApplePayItem';
import { ApplePayRequest } from '../../../types/applepay/ApplePayRequest';
import { IApplePaySession } from '../../../types/applepay/ApplePaySession';
import Spinner from '../../spinner/Spinner';
import { IApplePayConfigurationContext } from '../../../types/applepay/ApplePayConfigurationContext';

type IPaymentApplePayButton = RouteComponentProps & Partial<IContext>;

class ApplePayButton extends PureComponent<
  IPaymentApplePayButton &
    Partial<IContext> &
    Partial<ILocaleContext> &
    Partial<IApplePayConfigurationContext> & {
      isVisible: boolean;
      payment: PaymentPartial;
      paymentId;
      paymentMethodId?: string;
      paymentMethodType?: string;
      setOrderDescriptionVisibility?: (visible: boolean) => void;
    },
  { preTransition: boolean; appleSessionInProgress: boolean }
> {
  constructor(props) {
    super(props);
    this.state = {
      preTransition: true,
      appleSessionInProgress: false,
    };
  }

  componentDidMount() {
    requestAnimationFrame(() => {
      this.setState({ preTransition: false });
    });
  }

  clickHandler = () => {
    const {
      applePayConfiguration,
      paymentId,
      locale,
      paymentMethodId,
      payment,
      pmpt,
      setOrderDescriptionVisibility,
      history: { push },
      location: { pathname, search },
    } = this.props;

    const handleError = error => {
      window.location.reload();
      this.setState(() => {
        throw error;
      });
    };

    try {
      const { amount, currency } = payment;
      const selectedPaymentMethod = paymentMethodId || payment.selectedPaymentMethodId;
      const selectedPaymentMethodPType = pmpt || payment.selectedPmpt;

      if (!selectedPaymentMethod) {
        throw new Error('Payment method not selected');
      }

      if (!amount || !currency || !applePayConfiguration) {
        throw new Error('Error occurred with the payment');
      }

      const item: ApplePayItem = {
        type: applePayConfiguration.type,
        label: applePayConfiguration.label,
        amount: amount / 100,
        paymentTiming: applePayConfiguration.paymentTiming,
      };

      const request: ApplePayRequest = {
        supportedNetworks: applePayConfiguration.supportedNetworks,
        merchantCapabilities: applePayConfiguration.merchantCapabilities,
        countryCode: applePayConfiguration.countryCode,
        currencyCode: currency,
        total: item,
      };

      const session: IApplePaySession = new window.ApplePaySession(4, request);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      session.onvalidatemerchant = event => {
        getApplePayPaymentSession(paymentId)
          .then(merchantSession => {
            session.completeMerchantValidation(merchantSession);
          })
          .catch(error => handleError(error));
      };

      session.onpaymentauthorized = event => {
        const { paymentData } = event.payment.token;
        updatePayment(paymentId, {
          locale,
          paymentMethodId: selectedPaymentMethod,
          paymentMethodToken: Buffer.from(JSON.stringify(paymentData)).toString('base64'),
          pmpt: selectedPaymentMethodPType,
        })
          .then(({ redirectUrl, status }) => {
            session.completePayment(session.STATUS_SUCCESS);
            setTimeout(() => {
              if (redirectUrl) {
                window.location.replace(redirectUrl);
              } else {
                push(`${pathname}/callback${search}&selectedPaymentMethodType=APPLE_PAY`, { status });
              }
            }, 500);
          })
          .catch(error => handleError(error));
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      session.onpaymentmethodselected = event => {
        session.completePaymentMethodSelection({
          newTotal: item,
        });
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      session.oncancel = event => {
        this.setState({ appleSessionInProgress: false });
        if (setOrderDescriptionVisibility) setOrderDescriptionVisibility(true);
      };

      session.begin();
      this.setState({ appleSessionInProgress: true });
    } catch (error) {
      handleError(error);
    }
  };

  render() {
    const { isVisible, locale, isApplePayScriptLoaded } = this.props;
    const { preTransition, appleSessionInProgress } = this.state;
    return (
      <>
        {isVisible && (
          <ApplePayButtonContainer preTransition={preTransition} className="apple-pay-button-wrapper">
            {isApplePayScriptLoaded && !appleSessionInProgress ? (
              <>
                {/*  @ts-ignore */}
                <apple-pay-button
                  class="apple-pay-button apple-pay-button-fixed"
                  buttonstyle="black"
                  type="pay"
                  locale={locale}
                />
                <button
                  type="button"
                  onClick={this.clickHandler}
                  className="apple-pay-button-fixed apple-pay-button-overlay"
                  aria-label="Apple pay button"
                />
              </>
            ) : (
              <Spinner isLoading />
            )}
          </ApplePayButtonContainer>
        )}
      </>
    );
  }
}

export default withLocaleContext(withApplePayConfigurationContext(withContext(withRouter(ApplePayButton))));
